<template>
  <Popover>
    <PopoverTrigger>
      <slot />
    </PopoverTrigger>
    <PopoverContent class="w-auto p-3" align="start">
      <div class="flex flex-col gap-3">
        <!-- Recently Used -->
        <div v-if="recentColors.length">
          <p class="text-xs font-semibold text-muted-foreground mb-2">
            {{ $t("Недавние") }}
          </p>
          <div class="flex gap-1.5">
            <button
              v-for="c in recentColors"
              :key="'recent-' + c.color"
              class="color-circle"
              :style="{ backgroundColor: c.color }"
              @click="
                c.type === 'text'
                  ? applyTextColor(c.color)
                  : applyHighlight(c.color)
              "
            />
          </div>
        </div>

        <!-- Text Color -->
        <div>
          <p class="text-xs font-semibold text-muted-foreground mb-2">
            {{ $t("Цвет текста") }}
          </p>
          <div class="grid grid-cols-6 gap-1.5">
            <!-- Default -->
            <button
              class="color-text-btn"
              :class="{ active: !currentTextColor }"
              @click="resetTextColor"
            >
              <span class="text-foreground font-thin">A</span>
            </button>
            <button
              v-for="c in textColors"
              :key="'tc-' + c.color"
              class="color-text-btn"
              :class="{ active: currentTextColor === c.color }"
              @click="applyTextColor(c.color)"
            >
              <div :style="{ color: c.color }">A</div>
            </button>
          </div>
        </div>

        <!-- Highlight Color -->
        <div v-if="hasHighlight">
          <p class="text-xs font-semibold text-muted-foreground mb-2">
            {{ $t("Цвет выделения") }}
          </p>
          <div class="grid grid-cols-6 gap-1.5">
            <!-- Default (no highlight) -->
            <button
              class="color-circle color-circle--empty"
              :class="{ active: !currentHighlight }"
              @click="resetHighlight"
            />
            <button
              v-for="c in highlightColors"
              :key="'hc-' + c.color"
              class="color-circle"
              :class="{ active: currentHighlight === c.color }"
              :style="{ backgroundColor: c.color }"
              @click="applyHighlight(c.color)"
            />
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>

<script setup>
import { ref, computed } from "vue";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
});

const recentColors = ref([]);

const textColors = [
  { color: "#6B7280" },
  { color: "#92400E" },
  { color: "#EA580C" },
  { color: "#CA8A04" },
  { color: "#16A34A" },
  { color: "#2563EB" },
  { color: "#9333EA" },
  { color: "#DB2777" },
  { color: "#DC2626" },
  { color: "#9CA3AF" },
  { color: "#F97316" },
];

const highlightColors = [
  { color: "#F3F4F6" },
  { color: "#FEF3C7" },
  { color: "#FFEDD5" },
  { color: "#FEF9C3" },
  { color: "#DCFCE7" },
  { color: "#DBEAFE" },
  { color: "#EDE9FE" },
  { color: "#FCE7F3" },
  { color: "#FEE2E2" },
  { color: "#E0F2FE" },
  { color: "#F0ABFC" },
];

const hasHighlight = computed(() => {
  return !!props.editor?.commands?.toggleHighlight;
});

const currentTextColor = computed(() => {
  return props.editor?.getAttributes("textStyle")?.color || null;
});

const currentHighlight = computed(() => {
  return props.editor?.getAttributes("highlight")?.color || null;
});

function addToRecent(color, type) {
  const exists = recentColors.value.findIndex(
    (c) => c.color === color && c.type === type
  );
  if (exists >= 0) recentColors.value.splice(exists, 1);
  recentColors.value.unshift({ color, type });
  if (recentColors.value.length > 4) recentColors.value.pop();
}

function applyTextColor(color) {
  props.editor.chain().focus().setColor(color).run();
  addToRecent(color, "text");
}

function resetTextColor() {
  props.editor.chain().focus().unsetColor().run();
}

function applyHighlight(color) {
  props.editor.chain().focus().toggleHighlight({ color }).run();
  addToRecent(color, "highlight");
}

function resetHighlight() {
  props.editor.chain().focus().unsetHighlight().run();
}
</script>

<style scoped>
.color-text-btn {
  @apply w-6 h-6 rounded-md border border-border;
  @apply flex items-center justify-center;
  @apply text-xs  cursor-pointer;
  @apply transition-all duration-100;
}

.color-text-btn:hover {
  @apply scale-110 shadow-sm;
}

.color-text-btn.active {
  @apply ring-2 ring-primary ring-offset-1;
}

.color-circle {
  @apply w-5 h-5 rounded-full border border-border;
  @apply cursor-pointer;
  @apply transition-all duration-100;
}

.color-circle:hover {
  @apply scale-110 shadow-sm;
}

.color-circle.active {
  @apply ring-2 ring-primary ring-offset-1;
}

.color-circle--empty {
  @apply bg-background;
  background-image: linear-gradient(
    135deg,
    transparent 45%,
    hsl(var(--destructive)) 45%,
    hsl(var(--destructive)) 55%,
    transparent 55%
  );
}
</style>
